import { UserServiceClient } from 'fitbeat.api-clients';
import { FeatureToggle } from 'fitbeat.react-native-components';
import React from 'react';
import {
  ActivityIndicator, View, StyleSheet, Text, Platform,
} from 'react-native';
import { translate } from 'react-i18next';
import { connect } from 'react-redux';
import { RemoteConfigVariables, BodyMetricGraphTypes, UserTargetTypes } from 'fitbeat.models';
import { UserProfile } from 'fitbeat.gym-core';
import * as shape from 'd3-shape';
import TrackingLoadingError from './tracking-loading-error';
import { GoogleAnalytics } from '../../services/google-analytics';
import { Button, ButtonTypes, ButtonSize } from '../../components';
import { initialiseBodyScan } from '../../BodyScale/actions';
import { changeTarget, initialiseWeightTargetSet, removeTarget } from '../../WeightTarget/actions';
import { FirebaseRemoteConfig } from '../../utils/FirebaseRemoteConfig';
import { openInAppWebViewerInBackground } from '../../utils/inAppWebViewer';
import Theme from '../../theme';
import { TrackingImageTile } from './TrackingImageTile';
import scaleFocusImage from '../../assets/images/Fitbeat-focus-scale.jpg';
import { BodyMetricGraphTile } from './BodyMetricGraphTile';
import { EmptyBodyMetricGraphTile } from './EmptyBodyMetricGraphTile';
import {
  defaultScale, graphScaleLine, scaleImageAspectRatio, scaleText,
} from '../../utils/scaling';
import WorkoutTopImage from '../../assets/images/trackinghr.jpg';
import { BodyMetricGraph } from './BodyMetricGraph';
import { getApiConfig } from '../../config';
import { AuthTokenBuilder } from '../../utils/authTokenBuilder';
import { getTargetInfo } from '../../WeightTarget/selectors';

const isWeb = Platform.OS === 'web';

class TrackingHealthTile extends React.PureComponent {
  googleAnalytics = new GoogleAnalytics();

  constructor() {
    super();
  }

  getGraphInset = (graphType) => {
    if (graphType === BodyMetricGraphTypes.BodyfatHistory
      || graphType === BodyMetricGraphTypes.MusclemassHistory
      || graphType === BodyMetricGraphTypes.BodyweightHistory) {
      return {
        rightInset: defaultScale(5),
        leftInset: defaultScale(15),
        bottomInset: defaultScale(5),
        topInset: defaultScale(6),
      };
    }
    return {
      leftInset: defaultScale(15),
      rightInset: defaultScale(22),
      topInset: graphScaleLine(22),
      bottomInset: graphScaleLine(25),
    };
  };

  getLegendAndIcons = (graphType, data) => {
    const { t } = this.props;
    let legendAndIcons = [];
    if (graphType === BodyMetricGraphTypes.BodyweightTargetAndHistory
      || BodyMetricGraphTypes.MusclemassTargetAndHistory
    ) {
      const hasMilestone = data.some((x) => x.isMileStone);
      const hasTarget = data.some((x) => x.isTarget);
      legendAndIcons.push(
        {
          icon: <View style={[styles.circleLegendIcon, { backgroundColor: Theme.PRIMARY }]} />,
          label: <Text style={styles.legendText}>{t('workoutHistory:completed')}</Text>,
        },
      );
      if (hasMilestone) {
        legendAndIcons.push(
          {
            icon: <View style={styles.circleLegendIcon} />,
            label: <Text style={styles.legendText}>{t('workoutHistory:nextMilestone')}</Text>,
          },
        );
        if (hasTarget) {
          legendAndIcons.push(
            {
              icon: <View style={[styles.circleLegendIcon, { backgroundColor: '#CCCCCC' }]} />,
              label: <Text style={styles.legendText}>{t('workoutHistory:target')}</Text>,
            },
          );
        }
      }
    }
    if (graphType === BodyMetricGraphTypes.BodyfatHistory
        || graphType === BodyMetricGraphTypes.MusclemassHistory
        || graphType === BodyMetricGraphTypes.BodyweightHistory) {
      legendAndIcons = [
        {
          icon: <View style={[styles.scanIcon, { backgroundColor: Theme.PRIMARY }]} />,
          label: <Text style={[styles.legendText]}>{t('scans')}</Text>,
        },
        {
          icon: (
            <View style={styles.rangeIconContainer}>
              {[...Array(4)].map(() => <View style={styles.acceptableRangeIcon} />)}
            </View>
          ),
          label: <Text style={[styles.legendText, { marginRight: defaultScale(12) }]}>{t('acceptableRange')}</Text>,
        },
        {
          icon: (
            <View style={styles.rangeIconContainer}>
              {[...Array(4)].map(() => <View style={[styles.acceptableRangeIcon, { backgroundColor: '#2FF836' }]} />)}
            </View>
          ),
          label: <Text style={[styles.legendText, { marginRight: defaultScale(12) }]}>{t('suggestedRange')}</Text>,
        },
      ];
    }

    return legendAndIcons;
  };

  formatBodyMetricData = (bodyMetric) => {
    if (bodyMetric.graphType === BodyMetricGraphTypes.MusclemassTargetAndHistory
      || bodyMetric.graphType === BodyMetricGraphTypes.BodyfatTargetAndHistory
      || bodyMetric.graphType === BodyMetricGraphTypes.BodyweightTargetAndHistory
    ) {
      bodyMetric.data = bodyMetric.data.map((x) => ({ ...x, value: Math.round(x.value * 10) / 10 }));
    }

    return bodyMetric.data;
  };

  startWeightLog = () => {
    const { userProfile, initialiseWeightLog } = this.props;
    initialiseWeightLog(userProfile);
  };

  startBodyScan = () => {
    const { userProfile, initialiseScan } = this.props;
    initialiseScan(userProfile);
  };

  // Temp
  startBodyScanUsingStub = () => {
    const { userProfile, initialiseScan } = this.props;
    initialiseScan(userProfile, true);
  };

  openSmartScaleEcomm = async () => {
    const url = await FirebaseRemoteConfig.getRemoteConfigValue(
      RemoteConfigVariables.smart_scale_shop_url,
    );
    openInAppWebViewerInBackground(url);
  };

  graphTypeIsTargetAndHistory = (graphType) => (graphType === BodyMetricGraphTypes.MusclemassTargetAndHistory
            || graphType === BodyMetricGraphTypes.BodyweightTargetAndHistory
            || graphType === BodyMetricGraphTypes.BodyfatTargetAndHistory);

  renderSetTarget = () => {
    const { t } = this.props;
    return (
      <>
        <TrackingImageTile
          containerStyle={{ height: undefined, width: '100%', aspectRatio: scaleImageAspectRatio(1.55) }}
          imageSource={WorkoutTopImage}
          title={t('trackProgress')}
          useGradient
          // Revert this change to opacity once scan button is removed
          gradientHeight='100%'
          gradientOpacity={0.6}
          titleStyle={{
            paddingRight: defaultScale(100),
            paddingBottom: defaultScale(20),
          }}
          actions={
            !isWeb && (
            <Button
              title={t('tracking:SetYourTarget')}
              onPress={this.startWeightLog}
              fontColor={Theme.WHITE}
              size={ButtonSize.MEDIUM}
            />
            )
          }
        />
        <EmptyBodyMetricGraphTile
          graphHeading={t('tracking:TrackGoalTarget')}
          containerStyle={{ marginTop: 0, marginBottom: defaultScale(20) }}
        />
      </>
    );
  };

  renderSmartScale = (t, hasScale, userProfile) => (
    <TrackingImageTile
      imageSource={scaleFocusImage}
      title={t('smartScale')}
      containerStyle={{ height: undefined, width: '100%', aspectRatio: scaleImageAspectRatio(1.2) }}
      description={t('bodyScanErrorMessage')}
      useGradient
      actions={(
        <View>
          {!hasScale
            && (
            <Text onPress={this.openSmartScaleEcomm} style={[styles.link, { color: Theme.PRIMARY }]}>
              {t('bodyscan:noScaleLearnMore')}
            </Text>
            )}
          {
            !isWeb && (
            <>
              <Button
                title={hasScale ? t('tracking:startBodyScan') : t('setupSmartScaleNew')}
                onPress={this.startBodyScan}
                fontColor={Theme.WHITE}
                buttonType={ButtonTypes.NORMAL}
                size={ButtonSize.MEDIUM}
                containerStyle={{ marginVertical: defaultScale(10) }}
              />
              <FeatureToggle
                releaseType='alpha'
                userProfile={userProfile}
              >
                <Button
                  title='Scan using Stub'
                  onPress={this.startBodyScanUsingStub}
                  fontColor={Theme.PRIMARY}
                  buttonType={ButtonTypes.OUTLINE}
                  size={ButtonSize.MEDIUM}
                />
              </FeatureToggle>
            </>
            )
          }
        </View>
      )}
    />
  );

  render() {
    const {
      t, userProfile,
      bodyMetricStatsIsLoading,
      bodyMetricStatsLoadError,
      isLoadingHealth,
      navigation,
      bodyMetricSet,
      changeTarget,
      toggleRemoveTarget,
      target,
    } = this.props;

    const hasBodyScanStats = bodyMetricSet
      .filter((bodyMetric) => bodyMetric.graphType === BodyMetricGraphTypes.BodyfatHistory)
      .every((bodyMetric) => bodyMetric.data.length !== 0);
    const hasScale = UserProfile.hasSmartScale(userProfile);
    const isError = this.props.scanStats && this.props.scanStats.find((stat) => stat.error !== undefined);

    if (isError && isError.error === 'Requires a body scan') {
      isError.error = t('bodyScanErrorMessage');
    }

    if (isLoadingHealth) {
      return (
        <ActivityIndicator
          size='large'
          animating
          color={Theme.PRIMARY}
          style={styles.loader}
        />
      );
    }

    const kingMetricGraph = bodyMetricSet[0];
    const targetIsSet = !!target && !target.reached;

    const disableChangeTarget = !hasScale
      && (target?.targetType === UserTargetTypes.Bodyfat
        || target?.targetType === UserTargetTypes.Musclemass);

    return (
      <>
        {!bodyMetricStatsIsLoading && bodyMetricStatsLoadError
          && <TrackingLoadingError containerStyle={styles.noBorder} />}
        {!bodyMetricStatsIsLoading && !bodyMetricStatsLoadError
          && (
          <View>
            {!targetIsSet ? this.renderSetTarget() : null}
            {!targetIsSet && this.renderSmartScale(t, hasScale, userProfile)}
            {
              bodyMetricSet.map((bodyMetric) => {
                const graphInset = this.getGraphInset(bodyMetric.graphType);
                if (bodyMetric.data.length > 0) {
                  return (
                    <>
                      <BodyMetricGraphTile
                        key={bodyMetric.graphType}
                        graphHeading={t(`tracking:${BodyMetricGraphTypes[bodyMetric.graphType]}`)}
                        navigation={navigation}
                        legendAndIcons={this.getLegendAndIcons(bodyMetric.graphType, bodyMetric.data)}
                        graph={(
                          <BodyMetricGraph
                            verticalGridLines={bodyMetric.verticalGridLines}
                            ranges={bodyMetric.ranges}
                            minDate={bodyMetric.minDate}
                            maxDate={bodyMetric.maxDate}
                            graphType={bodyMetric.graphType}
                            data={this.formatBodyMetricData(bodyMetric)}
                            clippingPoint={bodyMetric.clippingPoint}
                            curve={this.graphTypeIsTargetAndHistory(bodyMetric.graphType)
                              ? shape.curveMonotoneX : shape.curveLinear}
                            unit={bodyMetric.unit}
                            showGraphText={this.graphTypeIsTargetAndHistory(bodyMetric.graphType)}
                            showCirclesForAllPoints={!this.graphTypeIsTargetAndHistory(bodyMetric.graphType)}
                            useRanges={!this.graphTypeIsTargetAndHistory(bodyMetric.graphType)}
                            {...graphInset}
                          />
                      )}
                        actions={this.graphTypeIsTargetAndHistory(bodyMetric.graphType)
                        && (
                        <>
                          {hasScale
                            && !isWeb
                            && (
                            <Button
                              title={t('tracking:startBodyScan')}
                              onPress={this.startBodyScan}
                              fontColor={Theme.WHITE}
                              buttonType={ButtonTypes.NORMAL}
                              containerStyle={{ marginVertical: defaultScale(10) }}
                              size={ButtonSize.NORMAL}
                            />
                            )}
                          {!hasScale && bodyMetric.graphType === BodyMetricGraphTypes.BodyweightTargetAndHistory
                            && (
                            <Button
                              title={t('tracking:LogWeightNow')}
                              onPress={this.startWeightLog}
                              fontColor={Theme.WHITE}
                              buttonType={ButtonTypes.NORMAL}
                              containerStyle={{ marginVertical: defaultScale(10), marginTop: defaultScale(30) }}
                              size={ButtonSize.NORMAL}
                            />
                            )}
                          <View style={styles.targetOptions}>
                            {!disableChangeTarget
                              && (
                              <Button
                                title={t('tracking:changeTarget')}
                                onPress={changeTarget}
                                fontColor={Theme.PRIMARY}
                                buttonType={ButtonTypes.OUTLINE}
                                size={ButtonSize.NORMAL}
                                containerStyle={{
                                  flex: 1,
                                  marginRight: defaultScale(3),
                                }}
                              />
                              )}

                            <Button
                              title={t('tracking:removeTarget')}
                              onPress={toggleRemoveTarget}
                              fontColor={Theme.PRIMARY}
                              buttonType={ButtonTypes.OUTLINE}
                              size={ButtonSize.NORMAL}
                              containerStyle={{
                                flex: 1,
                                marginLeft: disableChangeTarget ? 0 : defaultScale(3),
                                marginTop: disableChangeTarget ? defaultScale(10) : 0,
                              }}
                            />
                          </View>
                        </>
                        )}
                      />
                      {targetIsSet
                      && !hasScale
                      && this.graphTypeIsTargetAndHistory(
                        bodyMetric.graphType,
                      ) && (
                      <View style={{ marginTop: defaultScale(10) }}>
                        {this.renderSmartScale(t, hasScale, userProfile)}
                      </View>
                      )}
                    </>
                  );
                }
              })
            }
          </View>
          )}
        {bodyMetricStatsIsLoading
          && (
          <ActivityIndicator
            size='large'
            animating
            color={Theme.PRIMARY}
            style={[styles.loader, { height: defaultScale(100) }]}
          />
          )}
        {
          bodyMetricSet.map((bodyMetric) => {
            if (bodyMetric.data.length === 0 && bodyMetric.graphType === BodyMetricGraphTypes.BodyfatHistory) {
              return (
                <EmptyBodyMetricGraphTile
                  key={bodyMetric.graphType}
                  graphHeading={t('tracking:BodyfatHistory')}
                  openSmartScaleEcomm={this.openSmartScaleEcomm}
                />
              );
            }
            if (bodyMetric.data.length === 0 && bodyMetric.graphType === BodyMetricGraphTypes.MusclemassHistory) {
              return (
                <EmptyBodyMetricGraphTile
                  key={bodyMetric.graphType}
                  graphHeading={t('tracking:MusclemassHistory')}
                  openSmartScaleEcomm={this.openSmartScaleEcomm}
                />
              );
            }
          })
        }
      </>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingVertical: defaultScale(10),
    paddingRight: defaultScale(6),
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  icon: {
    alignSelf: 'center',
  },
  title: {
    color: Theme.BLACK,
    ...Theme.textStyle.heading,
  },
  healthTile: {
    width: '30%',
    marginTop: defaultScale(15),
    marginBottom: 0,
  },
  text: {
    ...Theme.graphStyle.customBold22,
    color: Theme.GREY_DARK,
  },
  noBorder: {
    borderWidth: 0,
    backgroundColor: 'transparent',
  },
  loader: {
    height: defaultScale(300),
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: defaultScale(20),
    alignSelf: 'center',
  },
  circleLegendIcon: {
    width: graphScaleLine(11),
    height: graphScaleLine(11),
    borderRadius: graphScaleLine(6),
    marginRight: defaultScale(6),
    alignSelf: 'center',
    backgroundColor: Theme.BLACK,
  },
  legendText: {
    ...Theme.graphStyle.regular12,
    color: Theme.BLACK,
    marginRight: defaultScale(30),
  },
  scanIcon: {
    width: defaultScale(15),
    height: defaultScale(2),
    alignSelf: 'center',
    marginRight: defaultScale(6),
  },
  acceptableRangeIcon: {
    width: defaultScale(2),
    backgroundColor: Theme.PRIMARY,
    height: defaultScale(2),
    alignSelf: 'center',
    marginRight: defaultScale(3),
  },
  rangeIconContainer: {
    marginRight: defaultScale(6),
    flexDirection: 'row',
  },
  link: {
    fontFamily: Theme.Weight700,
    fontSize: scaleText(16),
    textAlign: 'center',
    marginBottom: defaultScale(25),
  },
  targetOptions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
});

const mapStateToProps = (state) => {
  const {
    lastScanDate, bodyMetricStatsIsLoading, isLoadingHealth, bodyMetricStatsLoadError,
  } = state.tracking.view;
  const { bodyMetricStats, bodyMetricSet } = state.tracking.data;
  return {
    lastScanDate,
    bodyMetricStats,
    bodyMetricStatsIsLoading,
    isLoadingHealth,
    bodyMetricStatsLoadError,
    bodyMetricSet,
    target: getTargetInfo(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  const userServiceClient = new UserServiceClient(
    getApiConfig(),
    fetch,
    console.log,
    new AuthTokenBuilder(),
    GoogleAnalytics,
  );
  return {
    initialiseScan: (userProfile, testingStub = false) => dispatch(initialiseBodyScan(userProfile, testingStub)),
    initialiseWeightLog: (userProfile) => dispatch(initialiseWeightTargetSet(userProfile)),
    changeTarget: () => dispatch(changeTarget(userServiceClient)),
    toggleRemoveTarget: () => dispatch(removeTarget()),
  };
};

TrackingHealthTile = connect(mapStateToProps, mapDispatchToProps)(TrackingHealthTile);
export default translate(['workoutHistory', 'tracking', 'bodyscan'], { wait: true })(TrackingHealthTile);
